/**
 * CR408: Gate Patterns Admin UI — Promote / Disable / Inspect
 */

import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Eye, ArrowUpCircle, XCircle, RotateCcw, Shield } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface GatePattern {
  id: string;
  hostname: string;
  status: string;
  gate_type: string;
  vendor_hint: string | null;
  fingerprint_hash: string;
  success_count: number;
  fail_count: number;
  consecutive_failures: number;
  last_used_at: string | null;
  last_promoted_at: string | null;
  last_degraded_at: string | null;
  created_at: string;
  notes: string | null;
  recipe_template_id: string | null;
  recipe_name?: string;
  steps_json?: unknown;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-yellow-500/20 text-yellow-700 border-yellow-500/30",
  ACTIVE: "bg-green-500/20 text-green-700 border-green-500/30",
  DEGRADED: "bg-orange-500/20 text-orange-700 border-orange-500/30",
  DISABLED: "bg-muted text-muted-foreground border-border",
};

const GatePatterns = () => {
  const { isAdmin } = useAuth();
  const [patterns, setPatterns] = useState<GatePattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [inspectPattern, setInspectPattern] = useState<GatePattern | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadPatterns = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("gate_patterns")
        .select("*")
        .order("last_used_at", { ascending: false, nullsFirst: false });

      if (statusFilter !== "ALL") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;

      // Fetch linked template names + steps
      const templateIds = [...new Set((data || []).map(p => p.recipe_template_id).filter(Boolean))];
      const templatesMap: Record<string, { name: string; steps: unknown }> = {};

      if (templateIds.length > 0) {
        const { data: templates } = await supabase
          .from("web_recipe_templates")
          .select("id, name, steps_json")
          .in("id", templateIds);

        for (const t of templates || []) {
          templatesMap[t.id] = { name: t.name, steps: t.steps_json };
        }
      }

      setPatterns((data || []).map(p => ({
        ...p,
        recipe_name: p.recipe_template_id ? templatesMap[p.recipe_template_id]?.name : undefined,
        steps_json: p.recipe_template_id ? templatesMap[p.recipe_template_id]?.steps : undefined,
      })));
    } catch (err) {
      console.error("[CR408] Load error:", err);
      toast.error("Failed to load gate patterns");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  const updateStatus = async (patternId: string, newStatus: string) => {
    setActionLoading(patternId);
    try {
      const updateData: Record<string, unknown> = { status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === "ACTIVE") updateData.last_promoted_at = new Date().toISOString();
      if (newStatus === "DEGRADED") updateData.last_degraded_at = new Date().toISOString();
      if (newStatus === "ACTIVE") updateData.consecutive_failures = 0;

      const { error } = await supabase.from("gate_patterns").update(updateData).eq("id", patternId);
      if (error) throw error;

      // Log event
      const eventType = newStatus === "ACTIVE" ? "PROMOTED" : newStatus === "DISABLED" ? "DEGRADED" : "ATTEMPT";
      await supabase.from("gate_pattern_events").insert({
        pattern_id: patternId,
        event_type: eventType,
        details: { action: "manual", new_status: newStatus },
      });

      toast.success(`Pattern set to ${newStatus}`);
      await loadPatterns();
    } catch (err) {
      console.error("[CR408] Update error:", err);
      toast.error("Failed to update pattern");
    } finally {
      setActionLoading(null);
    }
  };

  const fmtTime = (t: string | null) => {
    if (!t) return "—";
    try { return formatDistanceToNow(new Date(t), { addSuffix: true }); } catch { return "—"; }
  };

  if (!isAdmin) {
    return <AdminLayout><p className="p-4 text-muted-foreground">Admin access required.</p></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Gate Patterns</h2>
            <Badge variant="outline" className="text-xs">{patterns.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="DEGRADED">Degraded</SelectItem>
                <SelectItem value="DISABLED">Disabled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadPatterns} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : patterns.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No gate patterns found.</p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <ScrollArea className="max-h-[70vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Hostname</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Gate / Vendor</TableHead>
                    <TableHead className="text-xs text-center">✓</TableHead>
                    <TableHead className="text-xs text-center">✗</TableHead>
                    <TableHead className="text-xs text-center">Consec.</TableHead>
                    <TableHead className="text-xs">Last Used</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patterns.map(p => (
                    <TableRow key={p.id} className="text-xs">
                      <TableCell className="font-mono max-w-[200px] truncate" title={p.hostname}>
                        {p.hostname}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[p.status] || ""}`}>
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">{p.gate_type}</span>
                        {p.vendor_hint && <span className="ml-1 text-foreground">/ {p.vendor_hint}</span>}
                      </TableCell>
                      <TableCell className="text-center text-green-600">{p.success_count}</TableCell>
                      <TableCell className="text-center text-red-500">{p.fail_count}</TableCell>
                      <TableCell className="text-center">
                        {p.consecutive_failures > 0 ? (
                          <span className="text-red-500 font-medium">{p.consecutive_failures}</span>
                        ) : "0"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{fmtTime(p.last_used_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-6 w-6"
                            onClick={() => setInspectPattern(p)}
                            title="View steps"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>

                          {p.status === "DRAFT" && (
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-green-600 hover:text-green-700"
                              onClick={() => updateStatus(p.id, "ACTIVE")}
                              disabled={actionLoading === p.id}
                              title="Promote to ACTIVE"
                            >
                              {actionLoading === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                            </Button>
                          )}

                          {(p.status === "ACTIVE" || p.status === "DRAFT" || p.status === "DEGRADED") && (
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => updateStatus(p.id, "DISABLED")}
                              disabled={actionLoading === p.id}
                              title="Disable"
                            >
                              {actionLoading === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                            </Button>
                          )}

                          {p.status === "DISABLED" && (
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-primary hover:text-primary"
                              onClick={() => updateStatus(p.id, "ACTIVE")}
                              disabled={actionLoading === p.id}
                              title="Re-enable (set ACTIVE)"
                            >
                              {actionLoading === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            </Button>
                          )}

                          {p.status === "DEGRADED" && (
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-primary hover:text-primary"
                              onClick={() => updateStatus(p.id, "ACTIVE")}
                              disabled={actionLoading === p.id}
                              title="Re-enable (set ACTIVE)"
                            >
                              {actionLoading === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}

        {/* Inspect Modal */}
        <Dialog open={!!inspectPattern} onOpenChange={() => setInspectPattern(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-sm font-mono">{inspectPattern?.hostname}</DialogTitle>
            </DialogHeader>
            {inspectPattern && (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[inspectPattern.status] || ""}`}>{inspectPattern.status}</Badge></div>
                    <div><span className="text-muted-foreground">Gate Type:</span> {inspectPattern.gate_type}</div>
                    <div><span className="text-muted-foreground">Vendor:</span> {inspectPattern.vendor_hint || "—"}</div>
                    <div><span className="text-muted-foreground">Recipe:</span> {inspectPattern.recipe_name || "—"}</div>
                    <div><span className="text-muted-foreground">Success:</span> <span className="text-green-600">{inspectPattern.success_count}</span></div>
                    <div><span className="text-muted-foreground">Failures:</span> <span className="text-red-500">{inspectPattern.fail_count}</span> (consec: {inspectPattern.consecutive_failures})</div>
                    <div><span className="text-muted-foreground">Last Used:</span> {fmtTime(inspectPattern.last_used_at)}</div>
                    <div><span className="text-muted-foreground">Created:</span> {fmtTime(inspectPattern.created_at)}</div>
                    {inspectPattern.last_promoted_at && <div><span className="text-muted-foreground">Promoted:</span> {fmtTime(inspectPattern.last_promoted_at)}</div>}
                    {inspectPattern.last_degraded_at && <div><span className="text-muted-foreground">Degraded:</span> {fmtTime(inspectPattern.last_degraded_at)}</div>}
                  </div>
                  {inspectPattern.notes && (
                    <div>
                      <span className="text-muted-foreground">Notes:</span>
                      <p className="mt-1">{inspectPattern.notes}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground font-medium">Steps (steps_json):</span>
                    <pre className="mt-1 p-3 bg-muted rounded-md overflow-x-auto text-[11px] font-mono whitespace-pre-wrap">
                      {inspectPattern.steps_json ? JSON.stringify(inspectPattern.steps_json, null, 2) : "No steps available"}
                    </pre>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Fingerprint:</span>
                    <code className="ml-1 text-[10px]">{inspectPattern.fingerprint_hash}</code>
                  </div>
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default GatePatterns;
